import React, { useEffect, useContext, createContext, ReactNode, useRef } from 'react';
import type { JSX } from 'react';
import { mockBeers150 as mockBeers } from '../../data/mockBeers150';
import {
  fetchBeersFromDirectus,
  fetchBeersFromDirectusByClientSlug,
  fetchBreweriesFromDirectus,
  fetchCidersFromDirectus,
  fetchCidersFromDirectusByClientSlug,
} from '../../services/directus/directusClient';
import { setDynamicBreweries } from '../../data/breweries';
import { loadFromLocalStorage } from '../storageUtils';
import { isBeerTried, isBeerFavorite } from '../beerHelpers';
import { BeerState, UserInteractionsState, Beer } from '../types';
import { FetchBeersStartAction, FetchBeersSuccessAction, FetchBeersFailureAction } from '../actions/beerDataActions';
import { getClientSlugFromHostname } from '../../utils/getClientSlugFromHostname';

const shouldUseDirectus = (): boolean => process.env.REACT_APP_USE_DIRECTUS !== 'false';

// Using any type assertions during the transition period

/**
 * Context for beer data operations
 */
const BeerDataContext = createContext<Record<string, never> | null>(null);

interface BeerDataProviderProps {
  children: ReactNode;
  dispatch: (action: any) => void;
  state: BeerState;
}

/**
 * Provider component for beer data operations
 * Responsible for loading beer data and user interactions from storage
 */
export function BeerDataProvider({ children, dispatch, state }: BeerDataProviderProps): JSX.Element {
  // Use a ref to track if we've already initialized
  const hasInitializedRef = useRef(false);
  
  // Load beers and user interactions on mount only
  useEffect(() => {
    // Prevent re-initialization on state changes
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    
    // Load user interactions from localStorage
    const loadUserInteractions = (): UserInteractionsState => {
      const favorites = loadFromLocalStorage('favorites', []) as string[];
      const ratings = loadFromLocalStorage('ratings', {}) as Record<string, number>;
      const notes = loadFromLocalStorage('notes', {}) as Record<string, string>;
      const tastingList = loadFromLocalStorage('tastingList', []) as string[];
      const triedList = loadFromLocalStorage('triedList', []) as string[];
      
      return {
        favorites,
        ratings,
        notes,
        tastingList,
        triedList,
      };
    };

    const userInteractions = loadUserInteractions();

    const loadBeers = async (): Promise<void> => {
      dispatch(new FetchBeersStartAction());
      
      try {
        let beers: Beer[];
        
        if (shouldUseDirectus()) {
          const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
          const clientSlug = getClientSlugFromHostname(hostname);

          // Fetch breweries first (needed for beer data)
          const fetchedBreweries = await fetchBreweriesFromDirectus();
          setDynamicBreweries(fetchedBreweries);
          
          // Fetch beers and ciders in parallel, then merge (ciders appended after beers)
          const [fetchedBeers, fetchedCiders] = await Promise.all([
            clientSlug ? fetchBeersFromDirectusByClientSlug(clientSlug) : fetchBeersFromDirectus(),
            clientSlug ? fetchCidersFromDirectusByClientSlug(clientSlug) : fetchCidersFromDirectus(),
          ]);
          beers = [...fetchedBeers, ...fetchedCiders];
        } else {
          await new Promise(resolve => setTimeout(resolve, 500));
          beers = mockBeers;
        }
        
        // Process beers in batches - dispatch first 20 to show UI quickly
        const INITIAL_BATCH_SIZE = 20;
        const firstBatch = beers.slice(0, INITIAL_BATCH_SIZE);
        
        const processedFirstBatch: Beer[] = firstBatch.map(beer => {
          const { favorites, ratings, notes } = userInteractions;
          const isFavorited = isBeerFavorite(beer.id, favorites);
          const rating = ratings[beer.id] || 0;
          const userNotes = notes[beer.id] || '';
          const tried = isBeerTried(beer.id, userInteractions);
          
          return {
            ...beer,
            isFavorite: isFavorited,
            userRating: rating,
            userNotes: userNotes,
            tried: tried,
          };
        });
        
        // Dispatch first batch immediately to hide loading screen
        dispatch(new FetchBeersSuccessAction(processedFirstBatch));
        
        // Process remaining beers in background
        if (beers.length > INITIAL_BATCH_SIZE) {
          setTimeout(() => {
            const remainingBeers = beers.slice(INITIAL_BATCH_SIZE);
            const processedRemaining: Beer[] = remainingBeers.map(beer => {
              const { favorites, ratings, notes } = userInteractions;
              const isFavorited = isBeerFavorite(beer.id, favorites);
              const rating = ratings[beer.id] || 0;
              const userNotes = notes[beer.id] || '';
              const tried = isBeerTried(beer.id, userInteractions);
              
              return {
                ...beer,
                isFavorite: isFavorited,
                userRating: rating,
                userNotes: userNotes,
                tried: tried,
              };
            });
            
            // Append remaining beers to existing ones
            const allProcessedBeers = [...processedFirstBatch, ...processedRemaining];
            dispatch(new FetchBeersSuccessAction(allProcessedBeers));
          }, 0);
        }
      } catch (error) {
        console.error('[BeerDataProvider] Error fetching beers:', error);
        dispatch(new FetchBeersFailureAction(error instanceof Error ? error : new Error('Unknown error')));
      }
    };
    
    // Load beers
    loadBeers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // Intentionally only depend on dispatch to prevent re-initialization
  
  // The context value is empty because this provider only handles side effects
  // It doesn't expose any values or methods to consumers
  return (
    <BeerDataContext.Provider value={{}}>
      {children}
    </BeerDataContext.Provider>
  );
}

/**
 * Custom hook to use the beer data context
 * @returns The beer data context value
 */
export function useBeerData(): Record<string, never> {
  const context = useContext(BeerDataContext);
  
  if (!context) {
    throw new Error('useBeerData must be used within a BeerDataProvider');
  }
  
  return context;
}
